import * as React from 'react'
import { useDispatch } from 'react-redux'
import {
  Box,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
} from '@mui/material'
import {
  getPseudonymTreeCount,
  disablePseudonymById,
  requestPseudonymHardDelete,
  confirmPseudonymHardDelete,
} from '../../../services'
import { setIsStored } from '../../../redux/slices/Alerts'
import VerificationCodeDialog from '../../VerificationCodeDialog'
import {
  setPendingDelete,
  getPendingDelete,
  clearPendingDelete,
} from '../../../utils/pendingDelete'

const PENDING_TABLE = 'pseudonym'

const isSuccessfulResponse = result => {
  if (!result) return false
  const hasExplicitFailure =
    result?.update === false ||
    result?.updated === false ||
    result?.success === false ||
    result?.deleted === false ||
    Boolean(result?.error) ||
    Boolean(result?.errors)
  return !hasExplicitFailure
}

const parseTreeCount = payload => {
  const candidates = [payload?.count, payload?.total, payload?.treeCount, payload?.data?.count]
  const found = candidates.find(value => Number.isFinite(Number(value)))
  return found === undefined ? null : Number(found)
}

/**
 * Flujo de eliminación de pseudónimo a nivel de tabla (pre-check de árboles +
 * OTP). Controlado de forma imperativa (`ref.openModal(pseudonym)`) para
 * sobrevivir a refetch/re-render del DataGrid mientras se lee el código.
 */
const DeletePseudonymModal = React.forwardRef((_props, ref) => {
  const dispatch = useDispatch()
  const [pseudonym, setPseudonym] = React.useState(null)

  const [dialogOpen, setDialogOpen] = React.useState(false)
  const [dialogLoading, setDialogLoading] = React.useState(false)
  const [dialogMode, setDialogMode] = React.useState('confirmDelete')
  const [dialogMessage, setDialogMessage] = React.useState('')
  const [associatedTreeCount, setAssociatedTreeCount] = React.useState(null)

  const [otpDialogOpen, setOtpDialogOpen] = React.useState(false)
  const [otpLoading, setOtpLoading] = React.useState(false)
  const [otpError, setOtpError] = React.useState(null)
  const [resendCount, setResendCount] = React.useState(0)
  const [retryAfterSeconds, setRetryAfterSeconds] = React.useState(null)

  const runPrecheck = React.useCallback(async targetPseudonym => {
    setDialogMode('confirmDelete')
    setDialogMessage('')
    setAssociatedTreeCount(null)
    setDialogOpen(true)
    setDialogLoading(true)

    try {
      const result = await getPseudonymTreeCount(targetPseudonym.id, targetPseudonym.nombre_comun)
      const treeCount = parseTreeCount(result)

      if (result?.error || treeCount === null) {
        setDialogMode('confirmDelete')
        setDialogMessage(result?.message || 'No se pudo verificar árboles asociados.')
      } else if (treeCount > 0) {
        setAssociatedTreeCount(treeCount)
        setDialogMode('suggestDisable')
        setDialogMessage(
          `El pseudónimo tiene ${treeCount} árbol(es) asociado(s), por lo que no puede eliminarse físicamente.`,
        )
      } else {
        setAssociatedTreeCount(0)
        setDialogMode('confirmDelete')
        setDialogMessage('')
      }
    } catch {
      setDialogMode('confirmDelete')
      setDialogMessage('No se pudo verificar árboles asociados.')
    } finally {
      setDialogLoading(false)
    }
  }, [])

  React.useImperativeHandle(ref, () => ({
    openModal: targetPseudonym => {
      if (!targetPseudonym?.id) return
      setPseudonym(targetPseudonym)
      setOtpError(null)
      runPrecheck(targetPseudonym)
    },
  }))

  // Restaurar el OTP tras una recarga (sin re-solicitar ni repetir el pre-check).
  React.useEffect(() => {
    const pending = getPendingDelete(PENDING_TABLE)
    if (pending) {
      setPseudonym({ id: pending.id, nombre_comun: pending.label })
      setOtpDialogOpen(true)
    }
  }, [])

  const handleDialogClose = () => {
    if (dialogLoading) return
    setDialogOpen(false)
    setDialogMode('confirmDelete')
    setDialogMessage('')
    setAssociatedTreeCount(null)
  }

  const handleDisable = async () => {
    if (!pseudonym) return
    setDialogLoading(true)
    try {
      const fallbackPayload = {
        nombre_comun: pseudonym.nombre_comun,
        idEspecie: pseudonym.idEspecie,
        habilitado: false,
      }
      const result = await disablePseudonymById(pseudonym.id, fallbackPayload)
      if (!isSuccessfulResponse(result)) {
        dispatch(setIsStored({ stored: false, message: result?.message || 'No se pudo deshabilitar el pseudónimo.' }))
      } else {
        dispatch(setIsStored({ stored: true, message: result?.message || 'Pseudónimo deshabilitado correctamente.' }))
      }
    } catch {
      dispatch(setIsStored({ stored: false, message: 'Error al deshabilitar el pseudónimo.' }))
    } finally {
      setDialogLoading(false)
      setDialogOpen(false)
      setDialogMode('confirmDelete')
      setDialogMessage('')
      setAssociatedTreeCount(null)
    }
  }

  const handleConfirmDelete = async () => {
    if (!pseudonym) return
    setDialogLoading(true)
    setDialogOpen(false)

    const result = await requestPseudonymHardDelete(pseudonym.id)
    setDialogLoading(false)

    if (result?.cooldownActive) {
      setRetryAfterSeconds(result.retryAfter ?? null)
      setOtpDialogOpen(true)
      setPendingDelete({ table: PENDING_TABLE, id: pseudonym.id, label: pseudonym.nombre_comun })
    } else if (result?.requested) {
      setRetryAfterSeconds(null)
      setResendCount(c => c + 1)
      setOtpDialogOpen(true)
      setPendingDelete({ table: PENDING_TABLE, id: pseudonym.id, label: pseudonym.nombre_comun })
    } else {
      dispatch(setIsStored({ stored: false, message: result?.message || 'No se pudo solicitar la eliminación.' }))
    }
  }

  const handleRequestNewCode = async () => {
    if (!pseudonym) return
    const result = await requestPseudonymHardDelete(pseudonym.id)
    if (result?.cooldownActive) {
      setRetryAfterSeconds(result.retryAfter ?? null)
      setOtpError('Espera antes de reenviar.')
    } else if (result?.requested) {
      setRetryAfterSeconds(null)
      setResendCount(c => c + 1)
      setOtpError(null)
    }
  }

  const handleOtpConfirm = async code => {
    if (!pseudonym) return
    setOtpLoading(true)
    setOtpError(null)
    const result = await confirmPseudonymHardDelete(pseudonym.id, code)
    setOtpLoading(false)

    if (result?.deleted) {
      setOtpDialogOpen(false)
      clearPendingDelete()
      dispatch(setIsStored({ stored: true, message: 'Pseudónimo eliminado permanentemente.' }))
    } else {
      setOtpError(result?.message || 'Código inválido o expirado.')
    }
  }

  const handleOtpClose = () => {
    if (otpLoading) return
    setOtpDialogOpen(false)
    setOtpError(null)
    clearPendingDelete()
  }

  return (
    <>
      <Dialog open={dialogOpen} onClose={handleDialogClose} maxWidth="xs" fullWidth>
        <DialogTitle color="primary.main" fontWeight={600}>
          {dialogLoading
            ? 'Procesando...'
            : dialogMode === 'suggestDisable'
              ? 'No se puede eliminar físicamente'
              : 'Eliminar pseudónimo'}
        </DialogTitle>

        <DialogContent>
          {dialogLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
              <CircularProgress size={32} />
            </Box>
          ) : dialogMode === 'suggestDisable' ? (
            <DialogContentText>
              {dialogMessage || 'El pseudónimo tiene árboles asociados y no puede eliminarse permanentemente.'}
              <br /><br />
              Puedes <strong>deshabilitar el pseudónimo</strong> para mantener integridad de datos.
            </DialogContentText>
          ) : (
            <DialogContentText>
              ¿Eliminar permanentemente el pseudónimo <strong>{pseudonym?.nombre_comun}</strong>?
              <br /><br />
              Se enviará un código de verificación a tu correo para confirmar.
              {dialogMessage ? <><br /><br />{dialogMessage}</> : null}
            </DialogContentText>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={handleDialogClose} disabled={dialogLoading}>
            Cancelar
          </Button>
          {!dialogLoading && dialogMode === 'confirmDelete' && (
            <Button onClick={handleConfirmDelete} variant="contained" color="error">
              Continuar
            </Button>
          )}
          {!dialogLoading && dialogMode === 'suggestDisable' && (
            <Button onClick={handleDisable} variant="contained" color="warning">
              {associatedTreeCount > 0
                ? `Deshabilitar pseudónimo (${associatedTreeCount} árboles asociados)`
                : 'Deshabilitar pseudónimo'}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      <VerificationCodeDialog
        open={otpDialogOpen}
        onClose={handleOtpClose}
        onConfirm={handleOtpConfirm}
        onRequestNew={handleRequestNewCode}
        loading={otpLoading}
        resourceLabel={pseudonym?.nombre_comun || ''}
        error={otpError}
        resendCount={resendCount}
        retryAfterSeconds={retryAfterSeconds}
      />
    </>
  )
})

DeletePseudonymModal.displayName = 'DeletePseudonymModal'

export default DeletePseudonymModal
